<template>
  <section class="treatment-imprint">

    <!-- Encarts empreintes validées/refusées pour le praticien -->
    <template v-if="!isAdmin">
      <div class="imprint-banner" :class="banner.look" v-if="banner.show">
        <p>{{ banner.title }}</p>
        <p>{{ banner.text }}</p>
      </div>
    </template>

    <!-- Cartes des empreintes -->
    <div class="card-container">
      <ImprintCard v-model="picturesImprint" v-if="picturesImprint" @sent:validation-request="isValidationRequestSent = true" @deleted="onImprintDeleted"/>
      <ImprintCard v-model="scanImprint" v-if="scanImprint" @sent:validation-request="isValidationRequestSent = true" @deleted="onImprintDeleted"/>

      <!-- Bouton d'ajout de photos si aucune empreinte photo n'existe -->
      <div class="add-imprint-card" v-if="!picturesImprint" @click="openAddModal('PICTURES')">
        <span class="add-imprint-icon">+</span>
        <p>Ajouter des photos</p>
      </div>

      <!-- Bouton d'ajout d'empreintes si aucune empreinte scan n'existe -->
      <div class="add-imprint-card" v-if="!scanImprint" @click="openAddModal('SCAN')">
        <span class="add-imprint-icon">+</span>
        <p>Ajouter des empreintes</p>
      </div>
    </div>

    <!-- Modale d'ajout d'empreintes -->
    <app-modal :show="showAddModal" :title="addModalTitle" @update:show="showAddModal = $event" :class="['add-imprint-modal', { 'add-imprint-modal--pictures': addModalType === 'PICTURES' }]" isAction>
      <template v-slot:modal-content>
        <!-- Choix du type d'empreintes (uniquement pour les scans) -->
        <div v-if="addModalType === 'SCAN'" class="add-imprint-type-choice">
          <p>Type d'empreintes :</p>
          <div class="radio-container">
            <app-radio v-model="newImprintType" :value="ImprintType.MATERIAL">Empreintes physiques</app-radio>
            <app-radio v-model="newImprintType" :value="ImprintType.INTERNAL_SCAN">Fichiers STL</app-radio>
            <app-radio v-model="newImprintType" :value="ImprintType.EXTERNAL_SCAN">Liens STL (plateforme externe)</app-radio>
          </div>

          <!-- Sous-type empreinte physique -->
          <div v-if="newImprintType === ImprintType.MATERIAL" class="material-type-choice">
            <p>Mat&#233;riau :</p>
            <div class="radio-container">
              <app-radio v-for="item in materialTypeOptions" :key="item.name" :value="item.name" v-model="newMaterialType">{{ item.label }}</app-radio>
            </div>
          </div>

          <!-- Upload fichiers STL -->
          <div v-if="newImprintType === ImprintType.INTERNAL_SCAN" class="stl-upload-zone">
            <app-upload @input="onStlFileSelected" accept=".stl" compress/>
            <div v-if="newScanFiles.length">Fichiers s&#233;lectionn&#233;s :</div>
            <app-file v-for="(file, idx) in newScanFiles" :key="idx" :file="file" @click:delete="newScanFiles.splice(idx, 1)"/>
          </div>
        </div>

        <!-- Upload de photos -->
        <div v-if="addModalType === 'PICTURES'" class="pictures-upload-zone">
          <p>S&#233;lectionnez vos photos (minimum 2) :</p>
          <div class="pictures-grid">
            <UploadPicture
              v-for="(pic, idx) in newPictures"
              :key="idx"
              :value="pic ? getUrl(pic) : null"
              :hint="idx === 0 ? 'Taille maximale : 20 Mo par photo' : null"
              @input="setPicture(idx, $event)"
            />
            <UploadPicture v-if="newPictures.filter(p => !!p).length < 8 && newPictures.every(p => !!p)" @input="addPicture($event)"/>
          </div>
        </div>
      </template>
      <template v-slot:modal-footer>
        <app-button look="secondary" @click="closeAddModal">Annuler</app-button>
        <app-button @click="submitNewImprint" :loading="isCreating" :disabled="!canSubmitNewImprint">Valider</app-button>
      </template>
    </app-modal>

  </section>
</template>

<script>
import auth from '@/services/auth';
import imprintApi from '@/services/api/imprint';
import utils from '@/services/utils/utils';
import ImprintType from '@/services/enums/imprint-type.enum';
import ImprintTypeLabel from '@/services/enums/imprint-type-label.enum';
import ImprintValidation from '@/services/enums/imprint-validation.enum';
import ImprintMaterialTypeLabel from '@/services/enums/imprint-material-type-label.enum';
import TreatmentType from '@/services/enums/treatment-type.enum';
import ImprintCard from '@/views/Treatments/components/ImprintCard.vue';
import UploadPicture from '@/views/Treatments/components/UploadPicture.vue';

export default {
  model: {
    prop: 'imprints',
    event: 'input',
  },
  name: 'treatment-imprint',
  components: {
    ImprintCard,
    UploadPicture,
  },
  props: {
    imprints: Array,
    treatmentType: String,
    treatmentStep: String,
    treatmentId: String,
  },
  data() {
    return {
      isValidationRequestSent: false,
      isAdmin: auth.isAdmin(),
      TreatmentType,
      ImprintType,
      ImprintTypeLabel,

      showAddModal: false,
      addModalType: null,
      newImprintType: null,
      newMaterialType: null,
      newScanFiles: [],
      newPictures: [null, null, null, null, null],
      isCreating: false,
      materialTypeOptions: utils.optionsFromEnum(ImprintMaterialTypeLabel),
    };
  },
  computed: {
    picturesImprint: {
      get() { return this.imprints.find((imprint) => imprint.imprintType === ImprintType.PICTURES); },
      set(value) {
        const index = this.imprints.findIndex((imprint) => imprint.imprintType === ImprintType.PICTURES);
        const newImprints = [...this.imprints];
        newImprints[index] = value;
        this.$emit('input', newImprints);
      },
    },
    scanImprint: {
      get() { return this.imprints.find((imprint) => imprint.imprintType !== ImprintType.PICTURES); },
      set(value) {
        const index = this.imprints.findIndex((imprint) => imprint.imprintType !== ImprintType.PICTURES);
        const newImprints = [...this.imprints];
        newImprints[index] = value;
        this.$emit('input', newImprints);
      },
    },
    addModalTitle() {
      return this.addModalType === 'PICTURES' ? 'Ajouter des photos' : 'Ajouter des empreintes';
    },
    canSubmitNewImprint() {
      if (this.addModalType === 'PICTURES') {
        return this.newPictures.filter((p) => !!p).length >= 2;
      }
      if (!this.newImprintType) return false;
      if (this.newImprintType === ImprintType.MATERIAL) return !!this.newMaterialType;
      if (this.newImprintType === ImprintType.INTERNAL_SCAN) return this.newScanFiles.length >= 1;
      if (this.newImprintType === ImprintType.EXTERNAL_SCAN) return true;
      return false;
    },
    banner() {
      const banner = {
        show: false,
        title: 'Empreintes non conformes',
        text: '',
        look: 'primary',
      };
      const hasRefused = this.imprints.find((imprint) => imprint.validation === ImprintValidation.REFUSED);
      const hasPending = this.imprints.find((imprint) => imprint.validation === ImprintValidation.PENDING);
      if (hasRefused) {
        banner.title = 'Empreintes non conformes';
        banner.look = 'secondary';
        banner.text = `Pour poursuivre votre demande de ${this.treatmentType === TreatmentType.GUTTER ? 'plan de traitement' : 'contention'}, merci de nous transmettre de nouvelles empreintes. Vous pouvez voir les indications Ealigner dans votre messagerie. Une fois les fichiers remplac\u00e9s ou renvoy\u00e9s par la poste, merci de demander une nouvelle v\u00e9rification.`;
        banner.show = true;
      } else if (hasPending) {
        banner.show = true;
        if (this.treatmentType === TreatmentType.GUTTER) {
          banner.title = 'Demande de v\u00e9rification re\u00e7ue';
          banner.text = 'Nous examinons les nouveaux \u00e9l\u00e9ments et reviendrons vers vous apr\u00e8s v\u00e9rification et validation.';
          banner.look = 'primary';
        } else {
          banner.title = 'En attente de validation de vos empreintes';
          banner.text = 'Notre \u00e9quipe \u00e9tudiera la conformit\u00e9 de vos empreintes d\u00e8s r\u00e9ception, avant de proc\u00e9der \u00e0 l\u2019exp\u00e9dition des contentions. Si les empreintes ne sont pas conformes, vous serez avertis.';
          banner.look = 'secondary';
        }
      }
      return banner;
    },
  },
  methods: {
    getUrl(file) {
      return file ? URL.createObjectURL(file) : null;
    },
    openAddModal(type) {
      this.addModalType = type;
      this.newImprintType = null;
      this.newMaterialType = null;
      this.newScanFiles = [];
      this.newPictures = [null, null, null, null, null];
      this.showAddModal = true;
    },
    closeAddModal() {
      this.showAddModal = false;
    },
    onStlFileSelected(file) {
      if (this.newScanFiles.length >= 4) {
        this.$message.show({
          title: 'Limite atteinte',
          text: 'Seulement 4 fichiers STL sont autoris\u00e9s.',
          confirmText: 'J\'ai compris',
        });
        return;
      }
      this.newScanFiles.push(file);
    },
    setPicture(index, file) {
      this.$set(this.newPictures, index, file);
    },
    addPicture(file) {
      const idx = this.newPictures.findIndex((p) => !p);
      if (idx !== -1) {
        this.$set(this.newPictures, idx, file);
      } else if (this.newPictures.length < 8) {
        this.newPictures.push(file);
      }
    },
    async submitNewImprint() {
      this.isCreating = true;
      try {
        const formData = new FormData();

        if (this.addModalType === 'PICTURES') {
          formData.append('imprintType', ImprintType.PICTURES);
          this.newPictures.filter((p) => !!p).forEach((pic) => formData.append('pictures', pic));
        } else {
          formData.append('imprintType', this.newImprintType);
          if (this.newImprintType === ImprintType.MATERIAL) {
            formData.append('materialType', this.newMaterialType);
          }
          if (this.newImprintType === ImprintType.INTERNAL_SCAN) {
            this.newScanFiles.forEach((file) => formData.append('scans', file));
          }
        }

        const response = await imprintApi.createImprint(this.treatmentId, formData);
        this.$emit('input', [...this.imprints, response.data]);
        this.$notification.show({ text: 'Empreinte ajout\u00e9e avec succ\u00e8s !' });
        this.closeAddModal();
      } catch (error) {
        this.$message.show({
          title: 'Erreur',
          text: 'Un probl\u00e8me est survenu lors de la cr\u00e9ation de l\'empreinte. Veuillez r\u00e9essayer plus tard.',
        });
      }
      this.isCreating = false;
    },
    onImprintDeleted(imprintId) {
      const newImprints = this.imprints.filter((imp) => imp.imprintId !== imprintId);
      this.$emit('input', newImprints);
    },
  },
};
</script>

<style lang="sass">
.treatment-imprint
  .imprint-banner
    width: 100%
    padding: 16px
    border-radius: $global-border-radius
    border: 1px solid currentcolor
    margin-bottom: 26px
    p
      margin: 0
      @include h6
      &:first-child
        @include h4
    &.primary
      color: $main-color
      background-color: $color-primary-20
      border-color: $color-primary-50
    &.secondary
      color: $color-secondary-100
      background-color: $color-secondary-20
      border-color: $color-secondary-40
  .card-container
    display: flex
    justify-content: space-between
    align-items: flex-start
    column-gap: 16px
    flex-wrap: wrap
    row-gap: 16px
    .imprint-card
      min-width: 40%
    .add-imprint-card
      min-width: 40%
      flex: 1
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      padding: 48px 24px
      border: 2px dashed $color-gray-40
      border-radius: $global-border-radius
      background: $white
      cursor: pointer
      transition: border-color 0.2s, background-color 0.2s
      &:hover
        border-color: $main-color
        background-color: $color-primary-20
      .add-imprint-icon
        font-size: 2rem
        color: $color-gray-60
        margin-bottom: 8px
      p
        margin: 0
        @include h5
        color: $color-gray-70
  .add-imprint-modal
    .modal-wrapper
      max-width: 600px
      height: auto
      max-height: calc(100vh - 40px)
      overflow-y: auto
    &.add-imprint-modal--pictures .modal-wrapper
      max-width: 900px
    .add-imprint-type-choice
      > p
        @include text-bold
        margin-bottom: 12px
      .radio-container
        display: flex
        flex-direction: column
        gap: 8px
        margin-bottom: 16px
    .material-type-choice
      > p
        @include text-bold
        margin-bottom: 8px
      .radio-container
        display: flex
        gap: 12px
    .stl-upload-zone
      > div
        @include h6
        color: $color-gray-70
        margin-top: 16px
      .app-file
        margin: 8px 0
    .pictures-upload-zone
      > p
        @include text-bold
        margin-bottom: 12px
      .pictures-grid
        display: flex
        flex-wrap: wrap
        gap: 12px
</style>
